﻿import { mutations } from "@/graphql";
import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { enqueueSnackbar } from "notistack";
import type { MutationUpdateSettingsArgs } from "@/types/graphql";

/**
 * Custom hook to update settings.
 * @param {Function} [onCompleted] - Callback function to be called when the mutation completes successfully.
 * @param {Function} [onError] - Callback function to be called when an error occurs during the mutation.
 * @returns {{saveSettings: Function, loading: boolean}} - The function to save settings and the loading state.
 */
export function useUpdateSettings(
  onCompleted: (data?: any) => void = () => null,
  onError: (error?: any) => void = () => null,
) {
  const [updateSettings, { loading }] = useMutation<
    any,
    MutationUpdateSettingsArgs
  >(mutations.updateSettings, {
    onCompleted: () => {
      onCompleted();
    },
    onError: (err) => {
      onError(err);

      enqueueSnackbar(err.message, {
        variant: "error",
      });
    },
    refetchQueries: ["settings"],
    errorPolicy: "all",
  });

  const saveSettings = useCallback(
    async (variables: MutationUpdateSettingsArgs) => {
      updateSettings({
        variables,
        refetchQueries: ["settings"],
      }).then();
    },
    [updateSettings],
  );

  return {
    saveSettings,
    loading,
  };
}
